import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { featureVisibilityService, type FeatureVisibilityConfig } from '@/services/featureVisibilityService';

const FEATURE_VISIBILITY_MODULE_ALIASES: Record<string, string> = {
  forecast: 'sop',
};

const FEATURE_VISIBILITY_ROUTE_ALIASES: Record<string, string> = {
  '/forecast': '/sop',
};

interface FeatureVisibilityContextType {
  environment: 'qa' | 'production';
  config: FeatureVisibilityConfig;
  loading: boolean;
  refresh: () => Promise<void>;
  isModuleVisible: (moduleKey: string, defaultValue?: boolean) => boolean;
  isRouteEnabled: (routePath: string, defaultValue?: boolean) => boolean;
  isActionEnabled: (actionKey: string, defaultValue?: boolean) => boolean;
}

const defaultConfig: FeatureVisibilityConfig = {};

const FeatureVisibilityContext = createContext<FeatureVisibilityContextType | null>(null);

export function FeatureVisibilityProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [environment, setEnvironment] = useState<'qa' | 'production'>('qa');
  const [config, setConfig] = useState<FeatureVisibilityConfig>(defaultConfig);
  // Start true so gated routes do not redirect to fallback before the first resolve.
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.id == null) {
      setConfig(defaultConfig);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await featureVisibilityService.getMe();
      setEnvironment(res.environment);
      setConfig(res.config || defaultConfig);
    } catch (error) {
      console.error('[FeatureVisibility] load error:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!isAuthenticated || user?.id == null) {
      setConfig(defaultConfig);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await featureVisibilityService.getMe();
        if (!cancelled) {
          setEnvironment(res.environment);
          setConfig(res.config || defaultConfig);
        }
      } catch (error) {
        console.error('[FeatureVisibility] load error:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user?.id]);

  const value = useMemo<FeatureVisibilityContextType>(() => ({
    environment,
    config,
    loading,
    refresh,
    isModuleVisible: (moduleKey: string, defaultValue = true) => {
      const direct = config.modules?.[moduleKey]?.visible;
      if (direct !== undefined) return direct;
      const legacyKey = FEATURE_VISIBILITY_MODULE_ALIASES[moduleKey];
      if (legacyKey) {
        const legacy = config.modules?.[legacyKey]?.visible;
        if (legacy !== undefined) return legacy;
      }
      return defaultValue;
    },
    isRouteEnabled: (routePath: string, defaultValue = true) => {
      const direct = config.routes?.[routePath]?.enabled;
      if (direct !== undefined) return direct;
      const legacyPath = FEATURE_VISIBILITY_ROUTE_ALIASES[routePath];
      if (legacyPath) {
        const legacy = config.routes?.[legacyPath]?.enabled;
        if (legacy !== undefined) return legacy;
      }
      return defaultValue;
    },
    isActionEnabled: (actionKey: string, defaultValue = true) =>
      config.actions?.[actionKey]?.enabled ?? defaultValue,
  }), [environment, config, loading, refresh]);

  return (
    <FeatureVisibilityContext.Provider value={value}>
      {children}
    </FeatureVisibilityContext.Provider>
  );
}

export function useFeatureVisibility() {
  const ctx = useContext(FeatureVisibilityContext);
  if (!ctx) throw new Error('useFeatureVisibility must be used within FeatureVisibilityProvider');
  return ctx;
}
